import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card, Col, DatePicker, Dropdown, Empty, Input, Pagination, Row, Select, Space, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { MenuProps } from 'antd';
import { ClearOutlined, DownloadOutlined, EyeOutlined, FileSearchOutlined, MoreOutlined, ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { fetchDocumentCenter, downloadDocumentCenterPdf, DocumentCenterItem, DocumentCenterType } from '../modules/document-center/api/documentCenterApi';
import { getDocumentCenterStatusColor, getDocumentCenterStatusLabel } from '../modules/document-center/documentCenterStatus';
import { groupDocumentsByOrder, type DocumentCenterGroup } from '../modules/document-center/documentCenterGrouping';
import {
  canSeeDocumentCenterAmounts,
  getDocumentCenterSubtitleKey,
  getVisibleDocumentCenterTypeFilters,
} from '../modules/document-center/documentCenterVisibility';
import { authService } from '../services/auth';
import { formatDate } from '../utils/dateUtils';
import { formatCurrency } from '../utils/currency';
import { useFeedback } from '../utils/feedback';
import { getApiErrorMessage } from '../utils/apiError';

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

// Orders (groups) shown per page; documents inside a group are never paginated.
const GROUPS_PER_PAGE = 10;

const GROUP_KIND_TAG: Record<DocumentCenterGroup['kind'], { color: string; labelKey: string }> = {
  CUSTOMER_ORDER: { color: 'blue', labelKey: 'documentCenter:groups.customerOrder' },
  SUPPLIER_ORDER: { color: 'gold', labelKey: 'documentCenter:groups.supplierOrder' },
  NONE: { color: 'default', labelKey: 'documentCenter:groups.noOrder' },
};

const buildDocumentDownloadFileName = (record: DocumentCenterItem): string => {
  const fileName = record.fileName || `${record.number || record.id}.pdf`;
  return fileName.replace(/[\\/:*?"<>|]+/g, '-');
};

// Label above each filter control — keeps the filter bar aligned and readable.
const FilterField: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <Text type="secondary" style={{ display: 'block', marginBottom: 4, fontSize: 12 }}>{label}</Text>
    {children}
  </div>
);

const DocumentCenter: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation(['documentCenter', 'common', 'pz', 'wz', 'invoices', 'orders']);
  const feedback = useFeedback();
  const [documents, setDocuments] = useState<DocumentCenterItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState<string | null>(null);
  const [type, setType] = useState<'ALL' | DocumentCenterType>('ALL');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<string | undefined>();
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null] | null>(null);
  const [groupPage, setGroupPage] = useState(1);
  // Distinct (status, representative type) pairs seen in the unfiltered result
  // set, used to populate the Status dropdown. Captured only when no status
  // filter is active so the option list doesn't collapse after a selection.
  const [statusPool, setStatusPool] = useState<Array<{ value: string; type: DocumentCenterType; workflow: boolean }>>([]);

  const canViewSalesDocuments = authService.canViewSalesDocuments();
  const canViewPurchaseDocuments = authService.canViewPurchaseDocuments();
  const canViewWarehouseDocuments = authService.canViewWarehouseDocuments();
  const documentCenterCapabilities = useMemo(() => ({
    canViewSalesDocuments,
    canViewPurchaseDocuments,
    canViewWarehouseDocuments,
  }), [canViewPurchaseDocuments, canViewSalesDocuments, canViewWarehouseDocuments]);
  const canSeeAmounts = canSeeDocumentCenterAmounts(documentCenterCapabilities);
  const documentCenterSubtitleKey = getDocumentCenterSubtitleKey(documentCenterCapabilities);

  const visibleTypeFilters = useMemo(
    () => getVisibleDocumentCenterTypeFilters(documentCenterCapabilities),
    [documentCenterCapabilities]
  );

  const groups = useMemo(() => groupDocumentsByOrder(documents), [documents]);
  const visibleGroups = useMemo(
    () => groups.slice((groupPage - 1) * GROUPS_PER_PAGE, groupPage * GROUPS_PER_PAGE),
    [groups, groupPage]
  );

  const statusSelectOptions = useMemo(
    () => {
      const options = statusPool
        .map(({ value, type: docType, workflow }) => ({ value, label: getDocumentCenterStatusLabel(t, docType, value, workflow) }))
        .sort((a, b) => a.label.localeCompare(b.label, 'pl'));
      // Safety net: a selected status must always carry a label even if it is no
      // longer in the current pool (e.g. after a type switch), so the Select
      // never shows a bare raw value.
      if (status && !options.some((option) => option.value === status)) {
        options.push({ value: status, label: status });
      }
      return options;
    },
    [statusPool, status, t]
  );

  const loadDocuments = async () => {
    setLoading(true);
    try {
      const [from, to] = dateRange || [];
      const data = await fetchDocumentCenter({
        type: type === 'ALL' ? undefined : type,
        search: search.trim() || undefined,
        status,
        fromDate: from ? from.startOf('day').toISOString() : undefined,
        toDate: to ? to.endOf('day').toISOString() : undefined,
      });
      setDocuments(data);
      setGroupPage(1);
      // Refresh the Status dropdown pool only from an unfiltered result set.
      if (!status) {
        const seen = new Map<string, { type: DocumentCenterType; workflow: boolean }>();
        data.forEach((doc) => {
          if (doc.status && !seen.has(doc.status)) seen.set(doc.status, { type: doc.type, workflow: Boolean(doc.workflow) });
        });
        setStatusPool(Array.from(seen, ([value, meta]) => ({ value, type: meta.type, workflow: meta.workflow })));
      }
    } catch (error: any) {
      feedback.error(getApiErrorMessage(error, t, 'documentCenter:errors.fetchFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  const resetFilters = () => {
    setSearch('');
    setStatus(undefined);
    setDateRange(null);
    setType('ALL');
  };

  const downloadPdf = async (record: DocumentCenterItem) => {
    if (!record.pdfPath) return;
    setDownloading(record.id);
    try {
      const blob = await downloadDocumentCenterPdf(record.pdfPath);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = buildDocumentDownloadFileName(record);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      feedback.error(
        getApiErrorMessage(error, t, 'documentCenter:errors.downloadFailed', undefined, {
          number: record.number || record.id,
        }),
      );
    } finally {
      setDownloading(null);
    }
  };

  const getRowMenuItems = (record: DocumentCenterItem): MenuProps['items'] => [
    { key: 'open', icon: <EyeOutlined />, label: t('documentCenter:actions.open') },
    {
      key: 'download',
      icon: <DownloadOutlined />,
      label: record.pdfPath ? t('documentCenter:actions.download') : t('documentCenter:actions.noPdf'),
      disabled: !record.pdfPath,
    },
  ];

  // Per-group document table. The order itself is the group header, so rows
  // carry only the document facts; the company column is shown solely in the
  // "no order" group, where the header has no party to name.
  const buildColumns = (includeCompany: boolean): ColumnsType<DocumentCenterItem> => [
    {
      title: t('documentCenter:columns.number'),
      dataIndex: 'number',
      key: 'number',
      width: 220,
      render: (number: string, record) => (
        <Space direction="vertical" size={0}>
          <Button type="link" style={{ padding: 0 }} onClick={() => navigate(record.sourcePath)}>
            {number || record.fileName || '-'}
          </Button>
          <Tag>{t(`documentCenter:types.${record.type}`)}</Tag>
        </Space>
      ),
    },
    {
      title: t('documentCenter:columns.date'),
      dataIndex: 'date',
      key: 'date',
      width: 130,
      render: (date?: string | null) => date ? formatDate(date) : '-',
    },
    ...(includeCompany ? [{
      title: t('documentCenter:columns.company'),
      key: 'company',
      render: (_: unknown, record: DocumentCenterItem) => (
        <Space direction="vertical" size={0}>
          <Text>{record.customerName || record.supplierName || '-'}</Text>
          {record.customerName && record.supplierName && (
            <Text type="secondary">{record.supplierName}</Text>
          )}
        </Space>
      ),
    }] : []),
    {
      title: t('documentCenter:columns.status'),
      dataIndex: 'status',
      key: 'status',
      width: 160,
      render: (value: string | null | undefined, record) => value
        ? <Tag color={getDocumentCenterStatusColor(value)}>{getDocumentCenterStatusLabel(t, record.type, value, Boolean(record.workflow))}</Tag>
        : '-',
    },
    ...(canSeeAmounts ? [{
      title: t('documentCenter:columns.amount'),
      key: 'amount',
      width: 150,
      align: 'right' as const,
      render: (_: unknown, record: DocumentCenterItem) => (
        record.amount != null ? formatCurrency(Number(record.amount), record.currency || 'PLN') : '-'
      ),
    }] : []),
    {
      title: t('documentCenter:columns.actions'),
      key: 'actions',
      width: 80,
      align: 'right',
      render: (_, record) => (
        <Dropdown
          trigger={['click']}
          menu={{
            items: getRowMenuItems(record),
            onClick: ({ key, domEvent }) => {
              domEvent.stopPropagation();
              if (key === 'open') {
                navigate(record.sourcePath);
              }
              if (key === 'download') {
                void downloadPdf(record);
              }
            },
          }}
        >
          <Button
            type="text"
            icon={<MoreOutlined />}
            loading={downloading === record.id}
            onClick={(event) => event.stopPropagation()}
            aria-label={t('common:actions')}
          />
        </Dropdown>
      ),
    },
  ];

  const renderGroup = (group: DocumentCenterGroup) => {
    const kindTag = GROUP_KIND_TAG[group.kind];
    return (
      <Card
        key={group.key}
        size="small"
        style={{ marginBottom: 12 }}
        title={(
          <Space wrap size={[8, 4]} style={{ padding: '4px 0' }}>
            <Tag color={kindTag.color} style={{ marginInlineEnd: 0 }}>{t(kindTag.labelKey)}</Tag>
            {group.orderPath ? (
              <Button type="link" style={{ padding: 0, fontWeight: 600 }} onClick={() => navigate(group.orderPath as string)}>
                {group.orderNumber}
              </Button>
            ) : null}
            {group.partyName ? <Text>{group.partyName}</Text> : null}
            {group.clientOrderNumber ? (
              <Text type="secondary">{t('documentCenter:groups.clientOrderNumber', { number: group.clientOrderNumber })}</Text>
            ) : null}
          </Space>
        )}
        extra={<Text type="secondary">{t('documentCenter:groups.documentsCount', { count: group.documents.length })}</Text>}
      >
        <Table
          rowKey={(record) => `${record.type}:${record.id}`}
          columns={buildColumns(group.kind === 'NONE')}
          dataSource={group.documents}
          size="small"
          pagination={false}
          scroll={{ x: 760 }}
        />
      </Card>
    );
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'flex-start', marginBottom: 16 }}>
        <div>
          <Title level={2} style={{ margin: 0 }}>{t('documentCenter:title')}</Title>
          <Text type="secondary">{t(documentCenterSubtitleKey)}</Text>
        </div>
        <Space wrap>
          <Button icon={<ReloadOutlined />} onClick={loadDocuments} loading={loading}>
            {t('common:refresh')}
          </Button>
        </Space>
      </div>

      <Card style={{ marginBottom: 16 }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} lg={6}>
            <FilterField label={t('documentCenter:filters.type')}>
              <Select
                value={type}
                style={{ width: '100%' }}
                onChange={setType}
                options={visibleTypeFilters.map((item) => ({
                  value: item.value,
                  label: t(item.labelKey),
                }))}
              />
            </FilterField>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <FilterField label={t('documentCenter:filters.searchLabel')}>
              <Input
                allowClear
                prefix={<SearchOutlined />}
                value={search}
                style={{ width: '100%' }}
                placeholder={t('documentCenter:filters.search')}
                onChange={(event) => setSearch(event.target.value)}
                onPressEnter={loadDocuments}
              />
            </FilterField>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <FilterField label={t('documentCenter:filters.status')}>
              <Select
                allowClear
                value={status}
                style={{ width: '100%' }}
                placeholder={t('documentCenter:filters.statusAll')}
                options={statusSelectOptions}
                onChange={(value) => setStatus(value || undefined)}
              />
            </FilterField>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <FilterField label={t('documentCenter:filters.dateRange')}>
              <RangePicker style={{ width: '100%' }} value={dateRange} onChange={(value) => setDateRange(value)} />
            </FilterField>
          </Col>
        </Row>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <Button type="primary" icon={<FileSearchOutlined />} onClick={loadDocuments}>
            {t('documentCenter:actions.search')}
          </Button>
          <Button icon={<ClearOutlined />} onClick={resetFilters}>
            {t('documentCenter:actions.clear')}
          </Button>
        </div>
      </Card>

      {loading && documents.length === 0 ? (
        <Card loading />
      ) : groups.length === 0 ? (
        <Card>
          <Empty description={t('documentCenter:empty')} />
        </Card>
      ) : (
        <>
          {visibleGroups.map(renderGroup)}
          {groups.length > GROUPS_PER_PAGE && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
              <Pagination
                current={groupPage}
                pageSize={GROUPS_PER_PAGE}
                total={groups.length}
                showSizeChanger={false}
                onChange={setGroupPage}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default DocumentCenter;
